import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Download, Trash2, Eye, X } from "lucide-react";

export default function FormSubmissions() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => api.get(`/admin/forms/${id}/submissions`).then(r => setData(r.data))
    .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, [id]);

  const remove = async (sid) => {
    if (!confirm("Supprimer cette soumission ?")) return;
    await api.delete(`/admin/forms/${id}/submissions/${sid}`);
    toast.success("Supprimée"); load();
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/forms/${id}/submissions/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.form?.slug || "form"}-submissions.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur d'export");
    }
  };

  if (!data) return <div className="p-12 text-center font-mono">Chargement…</div>;
  const { form, submissions } = data;

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <Link to="/admin/formulaires" className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</Link>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{form.title}</h1>
            <p className="text-sm text-slate-500 mt-1 font-mono">/f/{form.slug} · {submissions.length} soumissions</p>
          </div>
          <button onClick={exportCsv} data-testid="export-csv-btn" className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Download className="w-4 h-4"/> Exporter CSV
          </button>
        </div>

        {submissions.length === 0 ? (
          <div className="bg-white border border-slate-900/15 p-12 text-center text-slate-500 font-mono">Aucune soumission pour le moment.</div>
        ) : (
          <div className="bg-white border border-slate-900/15 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-xs text-slate-600">Date</th>
                  {form.fields.slice(0, 3).map(f => (
                    <th key={f.name} className="text-left p-3 font-bold uppercase tracking-wider text-xs text-slate-600">{f.label}</th>
                  ))}
                  <th className="text-right p-3 font-bold uppercase tracking-wider text-xs text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(s => (
                  <tr key={s.id} className="border-b border-slate-100" data-testid={`submission-${s.id}`}>
                    <td className="p-3 font-mono text-xs">{new Date(s.created_at).toLocaleString("fr-FR")}</td>
                    {form.fields.slice(0, 3).map(f => (
                      <td key={f.name} className="p-3 max-w-xs truncate">{Array.isArray(s.data[f.name]) ? s.data[f.name].join(", ") : (s.data[f.name] || "—")}</td>
                    ))}
                    <td className="p-3 text-right">
                      <button onClick={() => setSelected(s)} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white mr-1"><Eye className="w-3 h-3"/></button>
                      <button onClick={() => remove(s.id)} className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 className="w-3 h-3"/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <Footer />

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white border border-slate-900 w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl">Soumission du {new Date(selected.created_at).toLocaleString("fr-FR")}</h2>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5"/></button>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {form.fields.map(fld => (
                  <tr key={fld.name} className="border-b border-slate-100">
                    <td className="p-2 font-bold w-1/3 align-top">{fld.label}</td>
                    <td className="p-2 whitespace-pre-wrap">{Array.isArray(selected.data[fld.name]) ? selected.data[fld.name].join(", ") : (selected.data[fld.name] ?? "—")}</td>
                  </tr>
                ))}
                <tr className="border-b border-slate-100"><td className="p-2 font-bold">IP</td><td className="p-2 font-mono text-xs">{selected.ip || "—"}</td></tr>
                <tr><td className="p-2 font-bold">User-Agent</td><td className="p-2 font-mono text-xs break-all">{selected.user_agent || "—"}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
